import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { ApiError } from '../../lib/api';
import type { AccessEntry, EmailStatus, GrantRole, GrantScope, InviteExpiry } from '../../lib/sharing-types';
import { Button, Checkbox, CopyButton, Field, Input, Select } from '../../ui';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { shareKeys } from './keys';
import { PendingInvitations } from './PendingInvitations';

const GRANT_ROLES: GrantRole[] = ['viewer', 'commenter', 'editor'];
const INVITE_EXPIRIES: InviteExpiry[] = ['1d', '7d', '30d'];

/** Invite by email, then who has access (editable at this scope) and the pending invitations. */
export function PeopleTab({ scope }: { scope: GrantScope }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const access = useQuery({ queryKey: shareKeys.access(scope), queryFn: () => api.listAccess(scope) });
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: shareKeys.access(scope) }),
      queryClient.invalidateQueries({ queryKey: shareKeys.invitations(scope) }),
    ]);
  const ownKind = scope.type === 'project' ? 'project_member' : 'board_member';

  const update = useMutation({
    mutationFn: (e: AccessEntry & { role: GrantRole }) =>
      api.setGrant(scope, e.userId, { role: e.role, canExport: e.canExport, canDownload: e.canDownload, expiresAt: e.expiresAt }),
    onSettled: refresh,
  });
  const remove = useMutation({ mutationFn: (userId: string) => api.removeGrant(scope, userId), onSettled: refresh });
  // Who is asked about stays set while the dialog leaves, so its text holds under the exit animation.
  const [removing, setRemoving] = useState<AccessEntry | null>(null);
  const [asking, setAsking] = useState(false);

  return (
    <div className="flex flex-col gap-5">
      <InviteForm scope={scope} onInvited={refresh} />
      <section aria-label={t('sharing.access')} className="flex flex-col gap-2">
        <h3 className="font-sans text-sm font-semibold">{t('sharing.access')}</h3>
        {access.data?.length === 0 && <p className="font-sans text-sm text-fg-muted">{t('sharing.noAccess')}</p>}
        <ul className="flex flex-col gap-2">
          {(access.data ?? []).map((e) => (
            <li key={`${e.kind}-${e.userId}-${e.linkId ?? ''}`} className="flex flex-wrap items-center gap-2 font-sans text-sm">
              <span className="min-w-0 flex-1 truncate">{e.displayName || '—'}</span>
              {e.kind === ownKind ? (
                <>
                  <Select className="w-auto"
                    aria-label={t('sharing.role')}
                    value={e.role}
                    onChange={(ev) => update.mutate({ ...e, role: ev.target.value as GrantRole })}
                  >
                    {GRANT_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t(`sharing.roles.${r}`)}
                      </option>
                    ))}
                  </Select>
                  <Button
                    onClick={() => {
                      setRemoving(e);
                      setAsking(true);
                    }}
                  >
                    {t('sharing.remove')}
                  </Button>
                </>
              ) : (
                <span className="text-fg-muted">
                  {t(`sharing.kind.${e.kind}`)} · {t(`sharing.roles.${e.role}`)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </section>
      <PendingInvitations scope={scope} />
      {removing && (
        <ConfirmDialog
          open={asking}
          destructive
          title={t('p18.e.removeAccessTitle', { name: removing.displayName || '—' })}
          body={t('p18.e.removeAccessBody')}
          confirmLabel={t('p18.e.removeAccessConfirm')}
          cancelLabel={t('p18.e.cancel')}
          onConfirm={() =>
            remove
              .mutateAsync(removing.userId)
              .catch(() => undefined)
              .finally(() => setAsking(false))
          }
          onCancel={() => setAsking(false)}
        />
      )}
    </div>
  );
}

/** Email invitation at any scope; `roles` lists what the scope accepts (org: member/admin). */
export function InviteForm({
  scope,
  roles = GRANT_ROLES,
  defaultRole = 'commenter',
  flags = true,
  onInvited,
}: {
  scope: GrantScope | { type: 'org'; id: string };
  roles?: readonly string[];
  defaultRole?: string;
  flags?: boolean;
  onInvited?: () => unknown;
}) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [expires, setExpires] = useState<InviteExpiry>('7d');
  const [canExport, setCanExport] = useState(false);
  const [canDownload, setCanDownload] = useState(false);
  const [result, setResult] = useState<{ link: string; status: EmailStatus } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setError(t('sharing.invalidEmail'));
    setBusy(true);
    try {
      const r = await api.invite({
        scope, email: email.trim(), role, canExport, canDownload, expires, locale: i18n.language === 'fr' ? 'fr' : 'en',
      });
      setResult({ link: r.link, status: r.emailStatus });
      setEmail('');
      await queryClient.invalidateQueries({ queryKey: shareKeys.invitations(scope) });
      await onInvited?.();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      setError(t(code === 'already_member' ? 'sharing.alreadyMember' : code === 'invalid' ? 'sharing.invalidEmail' : 'sharing.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="flex flex-col gap-3" onSubmit={submit} noValidate>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-48 flex-1 flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('sharing.email')}
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('sharing.role')}
          <Select value={role} onChange={(e) => setRole(e.target.value)}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {t(`sharing.roles.${r}`)}
              </option>
            ))}
          </Select>
        </label>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('sharing.expires')}
          <Select value={expires} onChange={(e) => setExpires(e.target.value as InviteExpiry)}>
            {INVITE_EXPIRIES.map((x) => (
              <option key={x} value={x}>
                {t(`sharing.expiry.${x}`)}
              </option>
            ))}
          </Select>
        </label>
      </div>
      {flags && (
        <div className="flex flex-wrap gap-4">
          <Checkbox label={t('sharing.allowExport')} checked={canExport} onCheckedChange={setCanExport} />
          <Checkbox label={t('sharing.allowDownload')} checked={canDownload} onCheckedChange={setCanDownload} />
        </div>
      )}
      <div className="flex justify-end">
        <Button variant="primary" type="submit" disabled={busy}>
          {t('sharing.invite')}
        </Button>
      </div>
      {error && (
        <p role="alert" className="font-sans text-xs text-danger">
          {error}
        </p>
      )}
      {result && (
        <div className="flex flex-col gap-2 font-sans text-sm">
          <p role="status">{t(`sharing.emailStatus.${result.status}`)}</p>
          {/* No email may have left (no Resend key): the link is always there to read, select and copy. */}
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t('sharing.inviteLink')} className="min-w-48 flex-1">
              <Input readOnly value={result.link} onFocus={(e) => e.currentTarget.select()} />
            </Field>
            <CopyButton text={result.link} copiedLabel={t('sharing.copied')} failedLabel={t('sharing.copyFailed')}>
              {t('sharing.copyInvite')}
            </CopyButton>
          </div>
        </div>
      )}
    </form>
  );
}
